/**
 * Subject colour — single source of truth.
 *
 * These values previously lived in six places (tailwind.config.js, index.css
 * `.subject-badge-*`, Badge.tsx, ChildHome's SUBJECT_COLORS, ChildHome's inline
 * ACCENT/BG maps and GamePage's SUBJECT_ACCENT), which is how they drifted apart.
 * Badge.tsx, ChildHome.tsx and GamePage.tsx now read from here; the remaining
 * copies are still live and must be migrated before edits here propagate
 * everywhere: ParentDashboard.tsx, tailwind.config.js.
 *
 * Contrast: `ink` on `tint` is the badge combination and is measured to meet
 * WCAG AA for small text (>= 4.5:1). The previous `ink` values sat between
 * 2.86:1 and 4.24:1 — five of the six subjects failed. The hues are unchanged;
 * only the text tones were deepened until they passed.
 *
 * `accent` stays the vivid brand tone and is only ever used as a fill, dot or
 * border — never as small text on a light tint.
 */
import type { Subject } from '../types';

export interface SubjectTheme {
  /** Vivid brand tone. Fills, dots, borders — not small text on light. */
  accent: string;
  /** Text tone on `tint`. Meets AA for small text. */
  ink: string;
  /** Soft tint behind badges and pills. */
  tint: string;
  /** Tint edge, one step deeper than `tint`. */
  border: string;
  /** Near-white card surface carrying a hint of the hue. */
  surface: string;
  /** Tailwind gradient stops for hero cards. */
  gradient: string;
}

export const SUBJECT_THEME: Record<Subject, SubjectTheme> = {
  mathematics: {
    accent: '#F59E0B', ink: '#A75C05', tint: '#FEF3C7',
    border: '#FDE68A', surface: '#FFFBEB',
    gradient: 'from-[#FEF3C7] to-[#FDE68A]/40',
  },
  science: {
    accent: '#16A34A', ink: '#117E39', tint: '#DCFCE7',
    border: '#BBF7D0', surface: '#F0FDF4',
    gradient: 'from-[#DCFCE7] to-[#BBF7D0]/40',
  },
  technology: {
    accent: '#2563EB', ink: '#235EDF', tint: '#DBEAFE',
    border: '#BFDBFE', surface: '#EFF6FF',
    gradient: 'from-[#DBEAFE] to-[#BFDBFE]/40',
  },
  engineering: {
    accent: '#EF4444', ink: '#BF3636', tint: '#FEE2E2',
    border: '#FECACA', surface: '#FEF2F2',
    gradient: 'from-[#FEE2E2] to-[#FECACA]/40',
  },
  arts: {
    accent: '#9333EA', ink: '#9333EA', tint: '#F3E8FF',
    border: '#E9D5FF', surface: '#FAF5FF',
    gradient: 'from-[#F3E8FF] to-[#E9D5FF]/40',
  },
  futureskills: {
    accent: '#0D9488', ink: '#0B7B71', tint: '#CCFBF1',
    border: '#99F6E4', surface: '#F0FDFA',
    gradient: 'from-[#CCFBF1] to-[#99F6E4]/40',
  },
};

/** Falls back to mathematics for unknown/legacy subject strings. */
export function subjectTheme(subject: string): SubjectTheme {
  return SUBJECT_THEME[subject as Subject] ?? SUBJECT_THEME.mathematics;
}
